import React, {
  forwardRef,
  ForwardRefRenderFunction,
  RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import styled, { css } from "styled-components"
import useWindowEvent from "../../helpers/use-window-event.hook"
import { AppFunctionComponent, Language } from "../../types"
import { Footnote } from "../community/blog/blog.interface"
import { annotationStyles, footnotesAnchors } from "./general-styles.component"
import { SecondaryHeading } from "./heading.component"
import {
  NeutralRawLink,
  rawLinkStyles,
} from "../../ui/components/link/link.component"
import Section, { SectionLayout } from "./sections/section.component"
import useTranslationHook from "../../hooks/use-translation.hook"

const FootnotesContainer = styled.footer`
  a {
    color: ${({ theme }) => theme.color.tag};
  }
  ol {
    padding: 0;
    margin-top: 3rem;
    list-style: none;
    counter-reset: footnotes-counter;

    li {
      display: flex;
      position: relative;
      flex-direction: row;
      margin-bottom: 0.75em;
      font-size: ${12 / 16}rem;
      line-height: ${18 / 12}em;
      counter-increment: footnotes-counter;

      > *:first-child {
        min-width: 22px;
      }

      span {
        color: ${({ theme }) => theme.color.link};
        ${NeutralRawLink} {
          &[href^="#ref-"] {
            margin-right: 5px;
          }
        }
      }

      ${NeutralRawLink} {
        &:hover {
          text-decoration: underline;
        }
      }
    }

    span[id^="ft-"] {
      top: -140px;
      display: block;
      position: absolute;

      &:target {
        background-color: #000;
        + sup,
        + span {
          font-weight: 700;
        }
      }
    }
  }
  ${footnotesAnchors};
`

const ColumnsList = styled.ol<{ cols: number }>`
  text-align: left;
  margin-top: 0;
  ${({ cols }) =>
    cols === 2 &&
    css`
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.mobileHorizontal}px) {
        column-count: 2;
        column-gap: var(--grid-spacing);
      }
    `}
`

interface Props {
  footnotes: Footnote[]
  lang?: Language
  cols?: number
}

const FootnotesWithRef: ForwardRefRenderFunction<HTMLOListElement, Props> = (
  { footnotes, cols = 1 },
  ref
) => {
  return (
    <FootnotesContainer>
      <ColumnsList cols={cols} ref={ref}>
        {footnotes.map(
          (
            { footnoteAccessedDate, footnoteLink, footnoteTitle, occurrences },
            index
          ) => {
            const versions = Array.from({
              length: occurrences || 0,
            }).map((_, i) => String.fromCharCode(97 + i))

            return (
              <li key={index}>
                {versions.length > 1 ? (
                  <NeutralRawLink to={`#ref-${index + 1}-a`} target={"_self"}>
                    <span>{index + 1}.</span>
                  </NeutralRawLink>
                ) : (
                  <NeutralRawLink to={`#ref-${index + 1}-a`} target={"_self"}>
                    <span id={`ft-${index + 1}-a`} />
                    <span>{index + 1}.</span>
                  </NeutralRawLink>
                )}

                <span>
                  {versions.length > 1 &&
                    versions.map((v) => (
                      <NeutralRawLink
                        key={v}
                        to={`#ref-${index + 1}-${v}`}
                        target={"_self"}
                      >
                        <span id={`ft-${index + 1}-${v}`} />
                        <sup>{v}</sup>
                      </NeutralRawLink>
                    ))}
                  {footnoteLink ? (
                    <NeutralRawLink to={footnoteLink}>
                      {footnoteTitle}
                      {footnoteAccessedDate && footnoteAccessedDate && (
                        <span> Accessed: {footnoteAccessedDate}</span>
                      )}
                    </NeutralRawLink>
                  ) : (
                    <>
                      {footnoteTitle}
                      {footnoteAccessedDate && footnoteAccessedDate && (
                        <span> Accessed: {footnoteAccessedDate}</span>
                      )}
                    </>
                  )}
                </span>
              </li>
            )
          }
        )}
      </ColumnsList>
    </FootnotesContainer>
  )
}

const Hide = styled.button`
  ${annotationStyles};
  ${rawLinkStyles};
  margin: 0;
  border: none;
  outline: 0;
  cursor: pointer;
  appearance: none;
  background: transparent;
  text-align: right;

  &:hover {
    text-decoration: underline;
  }
`

const FootnotesWithHide = styled(Section)<{
  active: boolean
  containerHeight: number
}>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: "FootnotesHeading Hide" "Footnotes Footnotes";
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.color.borderLight};
  padding-bottom: 30px;
  ${({ active }) =>
    active &&
    css`
      padding-bottom: 70px;
    `}

  ${SecondaryHeading} {
    grid-area: FootnotesHeading;
    text-align: left;
  }
  ${Hide} {
    grid-area: Hide;
  }
  ${FootnotesContainer} {
    grid-area: Footnotes;
    height: ${({ containerHeight }) =>
      containerHeight + 48 + 16}px; /* + margin top and bottom */
    transition: height 0.4s linear;
    overflow: hidden;

    ${({ active }) =>
      !active &&
      css`
        height: 0px;
      `}
  }
`

export const Footnotes = forwardRef(FootnotesWithRef)

export const FootnotesSection: AppFunctionComponent<Props> = ({
  footnotes,
  lang = "en-US",
}) => {
  const footnotesRef = useRef<HTMLOListElement>(null)
  const getFootnotesHeight = (ref: RefObject<HTMLOListElement>) =>
    (ref.current && ref.current.getBoundingClientRect().height) || 0
  const [footnotesHeight, setFootnotesHeight] = useState(0)
  const { translate } = useTranslationHook()
  const [active, setActive] = useState(true)
  const changeActive = () => setActive(!active)

  useLayoutEffect(() => {
    setFootnotesHeight(getFootnotesHeight(footnotesRef))
  }, [])

  useWindowEvent("resize", () =>
    setFootnotesHeight(getFootnotesHeight(footnotesRef))
  )

  return (
    <FootnotesWithHide
      layout={SectionLayout.Text}
      active={active}
      containerHeight={footnotesHeight}
    >
      <SecondaryHeading renderAs={"h2"}>
        {translate("Resources")}
      </SecondaryHeading>
      <Hide onClick={changeActive}>{active ? "Hide" : "Show"}</Hide>
      <Footnotes
        footnotes={footnotes}
        lang={lang}
        cols={2}
        ref={footnotesRef}
      />
    </FootnotesWithHide>
  )
}

export default Footnotes
